/**
 * Ads Manager filter popup (handoff screens 2–6, 9).
 *
 * Four tabs — Metrics / Label / Country / Rules — each a Conditions card
 * built on the SAME components as Create rule (ConditionsCard, ConditionRow,
 * CombinatorPicker). The Metrics tab renders rule ConditionRows without
 * the cohort window and with four operators; Label / Country / Rules rows
 * fix the first field and use multiselects (Rules = the Label row with
 * `applied / not applied` and the rule picker, fix2). `draft` starts as a copy of the
 * applied filter; Apply normalizes it (invalid rows are dropped) and hands
 * it up; Cancel / ✕ / overlay / Escape ask "Discard changes?" only when
 * the normalized draft differs from what is applied.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  CombinatorPicker,
  ConditionRow,
  ConditionsCard,
  OPS,
} from "@/components/conditions/ConditionRow";
import type { EventMetricKindV2, EventTypeOptionV2, RuleConditionV2 } from "@/lib/api-hooks";
import {
  FILTER_TABS,
  TAB_LABEL,
  activeTabs,
  blankCountry,
  blankLabel,
  blankMetric,
  blankRule,
  counts as countsOf,
  normalize,
  sameFilter,
  totalCount,
  type AdsManagerFilter,
  type Connector,
  type FilterTab,
  type MetricCondition,
  type MetricOp,
  type RuleOp,
} from "@/lib/ads-manager-filter";
import { useDiscardGuard } from "@/lib/discard-guard";
import {
  CountryMultiSelect,
  LabelMultiSelect,
  type CountryOption,
  type LabelOption,
} from "./MultiSelect";
import { TypeIcon } from "./filter-icons";

const FILTER_OPS = OPS.filter((o) =>
  ["greater than", "less than", "equals", "not equals"].includes(o.id),
);
const OP_TO_RULE: Record<MetricOp, string> = {
  gt: "greater than", lt: "less than", eq: "equals", ne: "not equals",
};
const RULE_TO_OP: Record<string, MetricOp> = {
  "greater than": "gt", "less than": "lt", equals: "eq", "not equals": "ne",
};
// Share of Voice is attached to a row after the SQL (§64) and only at the
// keyword grain — the filter runs inside the SQL and can't read it.
const NOT_FILTERABLE = new Set(["Share of Voice", "SOV slot 2", "SOV total"]);

export type MetricEnv = {
  level: "campaign" | "ad_group" | "keyword";
  eventTypes?: readonly EventTypeOptionV2[];
  eventMetricKinds?: readonly EventMetricKindV2[];
};

function toRuleCond(m: MetricCondition): RuleConditionV2 {
  return {
    metric: m.metric,
    op: m.op ? OP_TO_RULE[m.op] : "",
    val: m.value == null ? "" : String(m.value),
    combinator: m.connector ? (m.connector.toLowerCase() as "and" | "or") : null,
  };
}

function fromRuleCond(r: RuleConditionV2): MetricCondition {
  const raw = r.val == null ? "" : String(r.val).trim().replace(",", ".");
  const num = raw === "" ? null : Number(raw);
  return {
    metric: r.metric,
    op: r.op ? (RULE_TO_OP[r.op] ?? "") : "",
    value: num != null && Number.isFinite(num) ? num : null,
    connector: r.combinator ? (r.combinator.toUpperCase() as Connector) : null,
  };
}

/** A tab always shows at least one (blank) row (TZ §5.2). */
function seeded(f: AdsManagerFilter, tab: FilterTab): AdsManagerFilter {
  if (f[tab].length > 0) return f;
  if (tab === "metrics") return { ...f, metrics: [blankMetric()] };
  if (tab === "label") return { ...f, label: [blankLabel()] };
  if (tab === "rules") return { ...f, rules: [blankRule()] };
  return { ...f, country: [blankCountry()] };
}

export function FilterModal({
  open,
  initialTab,
  applied,
  metricEnv,
  labels,
  countries,
  rules,
  onApply,
  onReset,
  onClose,
}: {
  open: boolean;
  initialTab: FilterTab;
  applied: AdsManagerFilter;
  metricEnv: MetricEnv;
  labels: LabelOption[];
  countries: CountryOption[];
  /** Rules of the app (all of the account in "all apps"); `badge` marks
   *  paused ones, `count` = entities of this level the rule is applied to. */
  rules: LabelOption[];
  onApply: (f: AdsManagerFilter) => void;
  onReset: () => void;
  onClose: () => void;
}) {
  const [draft, setDraft] = useState<AdsManagerFilter>(() => seeded(applied, initialTab));
  const [tab, setTab] = useState<FilterTab>(initialTab);
  const [panel, setPanel] = useState<{ tab: FilterTab; index: number } | null>(null);
  const wasOpen = useRef(false);

  // Fresh draft every time the popup opens (not on every applied change).
  useEffect(() => {
    if (open && !wasOpen.current) {
      setDraft(seeded(applied, initialTab));
      setTab(initialTab);
      setPanel(null);
    }
    wasOpen.current = open;
  }, [open, applied, initialTab]);

  const counts = useMemo(() => countsOf(draft), [draft]);
  const appliedCounts = useMemo(() => countsOf(applied), [applied]);
  const isDirty = !sameFilter(draft, applied);
  const tabDirty = (t: FilterTab) =>
    JSON.stringify(normalize({ ...draft, metrics: [], label: [], country: [], rules: [], [t]: draft[t] })) !==
    JSON.stringify(normalize({ ...applied, metrics: [], label: [], country: [], rules: [], [t]: applied[t] }));

  const { requestClose } = useDiscardGuard(
    open,
    isDirty,
    onClose,
    "Conditions you edited in this popup have not been applied yet.",
  );

  if (!open) return null;

  const selectTab = (t: FilterTab) => {
    setTab(t);
    setPanel(null);
    setDraft((d) => seeded(d, t));
  };
  const setRows = <K extends FilterTab>(t: K, rows: AdsManagerFilter[K]) =>
    setDraft((d) => ({ ...d, [t]: rows }));
  const addRow = () => {
    if (tab === "metrics") setRows("metrics", [...draft.metrics, blankMetric()]);
    else if (tab === "label") setRows("label", [...draft.label, blankLabel()]);
    else if (tab === "rules") setRows("rules", [...draft.rules, blankRule()]);
    else setRows("country", [...draft.country, blankCountry()]);
  };
  const removeRow = (t: FilterTab, i: number) => {
    setPanel(null);
    setDraft((d) => {
      const rows = (d[t] as { connector: Connector }[]).filter((_, j) => j !== i);
      const next = { ...d, [t]: rows } as AdsManagerFilter;
      return seeded(next, t);
    });
  };
  const clearTab = () => {
    setPanel(null);
    setDraft((d) => seeded({ ...d, [tab]: [] } as AdsManagerFilter, tab));
  };
  const apply = () => {
    onApply(normalize(draft));
  };
  const total = totalCount(draft);
  const summary = activeTabs(draft);

  const body = (
    <div className="af-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) requestClose(); }}>
      <div className="af-modal" role="dialog" aria-modal="true" aria-label="Filter">
        <div className="af-head">
          <div className="af-title">Filter</div>
          <button type="button" className="af-x" aria-label="Close" onClick={requestClose}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round"><path d="M6 6l12 12" /><path d="M6 18 18 6" /></svg>
          </button>
        </div>

        <div className="af-tabs">
          {FILTER_TABS.map((t) => {
            const n = counts[t];
            return (
              <button
                key={t}
                type="button"
                className="af-tab"
                data-active={tab === t ? "true" : "false"}
                data-set={n > 0 ? "true" : "false"}
                onClick={() => selectTab(t)}
              >
                <TypeIcon tab={t} size={14} />
                {TAB_LABEL[t]}
                {n > 0 && (
                  <span className="af-badge">
                    {n}
                    {tabDirty(t) && <span className="af-badge-dot" />}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <div className="af-body">
          <ConditionsCard
            className="af-card"
            borderColor="var(--c-accent-500)"
            background="var(--c-surface-alt)"
            title="Conditions"
            titleExtra={counts[tab] > 0 ? (
              <button type="button" className="af-clear" onClick={clearTab}>Clear</button>
            ) : null}
            onAdd={addRow}
            addLabel="+ Add condition"
          >
            {tab === "metrics" && draft.metrics.map((m, i) => (
              <ConditionRow
                key={i}
                value={toRuleCond(m)}
                index={i}
                onChange={(r) =>
                  setRows("metrics", draft.metrics.map((x, j) => (j === i ? fromRuleCond(r) : x)))
                }
                onDelete={() => removeRow("metrics", i)}
                canDelete
                level={metricEnv.level}
                eventTypes={metricEnv.eventTypes}
                eventMetricKinds={metricEnv.eventMetricKinds}
                ops={FILTER_OPS}
                showWindow={false}
                excludeMetrics={NOT_FILTERABLE}
              />
            ))}
            {tab === "label" && draft.label.map((r, i) => (
              <div key={i} style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {i > 0 && (
                  <CombinatorPicker
                    value={r.connector ? (r.connector.toLowerCase() as "and" | "or") : null}
                    onChange={(op) => setRows("label", draft.label.map((x, j) => (j === i ? { ...x, connector: op.toUpperCase() as Connector } : x)))}
                  />
                )}
                <div className="af-row">
                  <span className="af-fixed">Label is</span>
                  <select
                    className="select"
                    style={{ flex: "1 1 150px", minWidth: 0 }}
                    value={r.op}
                    onChange={(e) => setRows("label", draft.label.map((x, j) => (j === i ? { ...x, op: e.target.value as "contains" | "not_contains" } : x)))}
                  >
                    <option value="contains">contains</option>
                    <option value="not_contains">does not contain</option>
                  </select>
                  <LabelMultiSelect
                    options={labels}
                    selected={r.labelIds}
                    onChange={(ids) => setRows("label", draft.label.map((x, j) => (j === i ? { ...x, labelIds: ids } : x)))}
                    open={panel?.tab === "label" && panel.index === i}
                    onOpenChange={(o) => setPanel(o ? { tab: "label", index: i } : null)}
                  />
                  <button type="button" className="af-remove" title="Remove" onClick={() => removeRow("label", i)}>×</button>
                </div>
              </div>
            ))}
            {tab === "country" && draft.country.map((r, i) => (
              <div key={i} style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {i > 0 && (
                  <CombinatorPicker
                    value={r.connector ? (r.connector.toLowerCase() as "and" | "or") : null}
                    onChange={(op) => setRows("country", draft.country.map((x, j) => (j === i ? { ...x, connector: op.toUpperCase() as Connector } : x)))}
                  />
                )}
                <div className="af-row">
                  <span className="af-fixed">Country is</span>
                  <select
                    className="select"
                    style={{ flex: "1 1 150px", minWidth: 0 }}
                    value={r.op}
                    onChange={(e) => setRows("country", draft.country.map((x, j) => (j === i ? { ...x, op: e.target.value as "in" | "not_in" } : x)))}
                  >
                    <option value="in">is in</option>
                    <option value="not_in">is not in</option>
                  </select>
                  <CountryMultiSelect
                    options={countries}
                    selected={r.countries}
                    onChange={(codes) => setRows("country", draft.country.map((x, j) => (j === i ? { ...x, countries: codes } : x)))}
                    open={panel?.tab === "country" && panel.index === i}
                    onOpenChange={(o) => setPanel(o ? { tab: "country", index: i } : null)}
                  />
                  <button type="button" className="af-remove" title="Remove" onClick={() => removeRow("country", i)}>×</button>
                </div>
              </div>
            ))}
            {tab === "rules" && draft.rules.map((r, i) => (
              <div key={i} style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {i > 0 && (
                  <CombinatorPicker
                    value={r.connector ? (r.connector.toLowerCase() as "and" | "or") : null}
                    onChange={(op) => setRows("rules", draft.rules.map((x, j) => (j === i ? { ...x, connector: op.toUpperCase() as Connector } : x)))}
                  />
                )}
                <div className="af-row">
                  <span className="af-fixed">Rules is</span>
                  <select
                    className="select"
                    style={{ flex: "1 1 150px", minWidth: 0 }}
                    value={r.op}
                    onChange={(e) => setRows("rules", draft.rules.map((x, j) => (j === i ? { ...x, op: e.target.value as RuleOp } : x)))}
                  >
                    <option value="applied">applied</option>
                    <option value="not_applied">not applied</option>
                  </select>
                  <LabelMultiSelect
                    options={rules}
                    selected={r.ruleIds}
                    onChange={(ids) => setRows("rules", draft.rules.map((x, j) => (j === i ? { ...x, ruleIds: ids } : x)))}
                    open={panel?.tab === "rules" && panel.index === i}
                    onOpenChange={(o) => setPanel(o ? { tab: "rules", index: i } : null)}
                    noun="rules"
                    emptyText="No rules for this app"
                  />
                  <button type="button" className="af-remove" title="Remove" onClick={() => removeRow("rules", i)}>×</button>
                </div>
              </div>
            ))}
          </ConditionsCard>
        </div>

        {summary.length > 0 && (
          <div className="af-summary">
            <span className="af-summary-label">Will be applied:</span>
            {summary.map((t, i) => (
              <span key={t} style={{ display: "contents" }}>
                {i > 0 && <span className="af-and">AND</span>}
                <button type="button" className="af-pill" onClick={() => selectTab(t)}>
                  <TypeIcon tab={t} />
                  {TAB_LABEL[t]}
                  <span className="count">{counts[t]}</span>
                </button>
              </span>
            ))}
          </div>
        )}

        <div className="af-foot">
          <button
            type="button"
            className="af-btn"
            onClick={onReset}
            disabled={totalCount(applied) === 0 && total === 0}
            title={appliedCounts.metrics + appliedCounts.label + appliedCounts.country + appliedCounts.rules > 0 ? "Remove the applied filter" : undefined}
          >
            Reset filter
          </button>
          <div className="af-foot-right">
            <button type="button" className="af-btn cancel" onClick={requestClose}>Cancel</button>
            <button type="button" className="af-btn primary" onClick={apply}>
              {total > 0 ? `Apply (${total})` : "Apply"}
            </button>
          </div>
        </div>
      </div>

    </div>
  );
  return createPortal(body, document.body);
}
